import React from 'react'
import type { Block, Variable } from '../../types'
import { PropertyLabel, PropertyInput } from './shared'
import { Plus, Trash2, GripVertical } from 'lucide-react'

interface TabsPropertiesProps {
  block: Block
  variable: Variable
  linkedBlocks: Block[]
  onUpdateBlock: (id: string, updates: Partial<Block>) => void
  onUpdateVariable: (name: string, updates: Partial<Variable>) => void
  onRenameVariable: (oldName: string, newName: string) => void
}

interface Tab {
  label: string
  value: string
  content: string
}

export default function TabsProperties({
  block,
  variable,
  linkedBlocks,
  onUpdateBlock,
  onUpdateVariable,
  onRenameVariable,
}: TabsPropertiesProps) {
  const config = (block.config || {}) as Record<string, unknown>
  const tabs = (config.tabs as Tab[]) || []

  const updateTabs = (newTabs: Tab[]) => {
    onUpdateBlock(block.id, { config: { ...config, tabs: newTabs } })
  }

  const updateTab = (index: number, field: keyof Tab, val: string) => {
    const newTabs = tabs.map((tab, i) =>
      i === index ? { ...tab, [field]: val } : tab
    )
    updateTabs(newTabs)
  }

  const addTab = () => {
    const num = tabs.length + 1
    updateTabs([...tabs, { label: `Tab ${num}`, value: `tab_${num}`, content: '' }])
  }

  const removeTab = (index: number) => {
    if (tabs.length <= 1) return
    const removed = tabs[index]
    const newTabs = tabs.filter((_, i) => i !== index)
    updateTabs(newTabs)
    if (variable.default === removed.value) {
      onUpdateVariable(variable.name, { default: newTabs[0].value })
    }
  }

  return (
    <>
      <div className="space-y-2">
        <PropertyLabel>Linked Variable</PropertyLabel>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-[#5048e5] font-bold">@</span>
          <input
            type="text"
            value={variable.name}
            onChange={(e) =>
              onRenameVariable(variable.name, e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase())
            }
            className="w-full font-mono bg-slate-50 border border-slate-200 rounded-lg p-2.5 pl-7 text-sm focus:ring-2 focus:ring-[#5048e5] focus:border-transparent transition-all outline-none"
          />
        </div>
      </div>

      <div className="space-y-2">
        <PropertyLabel>Default Tab</PropertyLabel>
        <select
          value={String(variable.default)}
          onChange={(e) => onUpdateVariable(variable.name, { default: e.target.value })}
          className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#5048e5] focus:border-transparent transition-all outline-none"
        >
          {tabs.map((tab) => (
            <option key={tab.value} value={tab.value}>
              {tab.label}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-3">
        <PropertyLabel>Tabs</PropertyLabel>
        {tabs.map((tab, i) => (
          <div key={i} className="space-y-2 p-3 bg-slate-50 rounded-lg border border-slate-100">
            <div className="flex items-center gap-1.5">
              <GripVertical className="h-3.5 w-3.5 text-slate-300 shrink-0" />
              <input
                type="text"
                value={tab.label}
                onChange={(e) => updateTab(i, 'label', e.target.value)}
                placeholder="Tab label"
                className="flex-1 bg-white border border-slate-200 rounded-lg p-2 text-sm focus:ring-2 focus:ring-[#5048e5] focus:border-transparent transition-all outline-none"
              />
              <button
                onClick={() => removeTab(i)}
                disabled={tabs.length <= 1}
                className="p-1.5 hover:bg-slate-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="Remove tab"
              >
                <Trash2 className="h-3.5 w-3.5 text-slate-400" />
              </button>
            </div>
            <textarea
              value={tab.content}
              onChange={(e) => updateTab(i, 'content', e.target.value)}
              placeholder="Tab content... (use @variable for dynamic values)"
              rows={2}
              className="w-full bg-white border border-slate-200 rounded-lg p-2 text-sm focus:ring-2 focus:ring-[#5048e5] focus:border-transparent transition-all outline-none resize-none"
            />
          </div>
        ))}
        <button
          onClick={addTab}
          className="w-full py-2 border border-dashed border-slate-300 text-slate-500 text-xs font-bold uppercase tracking-wider rounded-lg hover:border-[#5048e5] hover:text-[#5048e5] transition-all flex items-center justify-center gap-1.5"
        >
          <Plus className="h-3.5 w-3.5" />
          Add Tab
        </button>
      </div>

      {linkedBlocks.length > 0 && (
        <div className="pt-4 border-t border-slate-100">
          <PropertyLabel>Linked Blocks</PropertyLabel>
          <ul className="space-y-1 mt-2">
            {linkedBlocks.map((lb) => (
              <li key={lb.id} className="text-sm text-slate-500">
                &bull; {lb.content?.slice(0, 30) || lb.label || lb.type}{' '}
                <span className="text-xs">({lb.type})</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  )
}
